import React from "react";
import { Field } from "formik";
import Select from "react-select";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const customStyles = {
  indicatorSeparator: () => ({ display: "none" }),
  control: (base, state) => ({
    ...base,
    width: "100%",
    border: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
    minHeight: "44px",
    cursor: "pointer",
  }),
  placeholder: (defaultStyles) => ({
    ...defaultStyles,
    color: "#94a3b8", // slate-400
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  singleValue: (defaultStyles) => ({
    ...defaultStyles,
    color: "#334155", // slate-700
    fontSize: "0.875rem",
    fontWeight: "500",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.75rem", // rounded-xl
    overflow: "hidden",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e2e8f0",
    zIndex: 9999,
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "#eef2ff", // indigo-50
    borderRadius: "0.375rem",
    padding: "1px 4px",
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "#4f46e5", // indigo-600
    fontWeight: "600",
    fontSize: "0.75rem",
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "#6366f1", // indigo-500
    borderRadius: "0.25rem",
    ":hover": {
      backgroundColor: "#e0e7ff", // indigo-100
      color: "#4338ca", // indigo-700
    },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "0.875rem",
    fontWeight: state.isSelected ? "600" : "400",
    color: state.isSelected ? "#ffffff" : "#334155",
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
        ? "#f1f5f9"
        : "transparent",
    cursor: "pointer",
    ":active": {
      backgroundColor: "#4338ca",
    },
  }),
};

export const FormDropDown = ({
  name = "select",
  label = "select field",
  options = [],
  required,
  placeholder,
  value,
  ...props
}) => {
  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const hasError = meta.touched && meta.error;

        return (
          <div className="w-full flex flex-col gap-2">
            {label && (
              <label
                className={`text-sm font-bold tracking-wide uppercase ${
                  hasError ? "text-red-500" : "text-slate-700"
                }`}
              >
                {formatUnderscoreToSpace(label)}
                {required && <span className="text-red-500 ms-0.5">*</span>}
              </label>
            )}

            <div
              className={`w-full border rounded-xl bg-white transition-all ${
                hasError
                  ? "border-red-500"
                  : "border-slate-200 hover:border-slate-300 focus-within:border-slate-400"
              }`}
            >
              <Select
                {...field}
                {...props}
                name={name}
                value={value}
                options={options}
                placeholder={
                  placeholder
                    ? formatUnderscoreToSpace(placeholder)
                    : "Select option"
                }
                onChange={(selectedOption) =>
                  form.setFieldValue(name, selectedOption?.value)
                }
                onBlur={() => form.setFieldTouched(name, true)}
                styles={customStyles}
                required={required}
              />
            </div>

            <div className="min-h-[1.25rem] ps-1">
              {hasError && (
                <span className="text-xs font-medium text-red-500 block">
                  {meta.error}
                </span>
              )}
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export const FormMultiSelectDropDown = ({
  name = "select",
  label = "select field",
  options = [],
  required,
  placeholder,
  value,
  ...props
}) => {
  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const hasError = meta.touched && meta.error;

        return (
          <div className="w-full flex flex-col gap-2">
            {label && (
              <label
                className={`text-sm font-bold tracking-wide uppercase ${
                  hasError ? "text-red-500" : "text-slate-700"
                }`}
              >
                {formatUnderscoreToSpace(label)}
                {required && <span className="text-red-500 ms-0.5">*</span>}
              </label>
            )}

            <div
              className={`w-full border rounded-xl bg-white transition-all ${
                hasError
                  ? "border-red-500"
                  : "border-slate-200 hover:border-slate-300 focus-within:border-slate-400"
              }`}
            >
              <Select
                {...field}
                {...props}
                name={name}
                isMulti
                value={value}
                options={options}
                placeholder={
                  placeholder
                    ? formatUnderscoreToSpace(placeholder)
                    : "Select multiple"
                }
                onChange={(selectedOptions) =>
                  form.setFieldValue(
                    name,
                    selectedOptions
                      ? selectedOptions.map((option) => option.value)
                      : [],
                  )
                }
                onBlur={() => form.setFieldTouched(name, true)}
                styles={customStyles}
                required={required}
              />
            </div>

            <div className="min-h-[1.25rem] ps-1">
              {hasError && (
                <span className="text-xs font-medium text-red-500 block">
                  {meta.error}
                </span>
              )}
            </div>
          </div>
        );
      }}
    </Field>
  );
};
